/**
 * Organization → Notifications: email alert subscriptions by event.
 */
import { useEffect, useMemo, useState } from "react";
import adminStyles from "../ui/AdminStyles";
import {
  authedDelete,
  authedGet,
  authedPost,
  authedPut,
} from "../api/customerAuthedFetch";
import InfoTip from "../components/InfoTip";
import { ThWithTip } from "../components/FieldTip";

const TIPS = {
  add:
    "Create an email subscription for an event that does not already have one. Each event can have one email subscription.",
  event: "Organization event that triggers the notification (for example job failures).",
  channel: "Delivery channel. Email is currently the only supported channel.",
  recipients: "Organization users who receive email when the event occurs.",
  status: "Enabled subscriptions send email; disabled ones are kept but do not send.",
  actions: "Edit recipients or enabled state, or delete the subscription.",
  formEvent: "Which event this subscription listens for. Cannot change after create.",
  formRecipients: "Select one or more users in your organization as email recipients.",
  formEnabled: "When unchecked, the subscription is saved but emails are not sent.",
};

const SECTION_CARD = {
  marginTop: 20,
  padding: "20px 22px 22px",
  border: "1px solid #e2e8f0",
  borderRadius: 12,
  background: "linear-gradient(180deg, #ffffff 0%, #fafbfc 100%)",
  boxShadow: "0 1px 2px rgba(15, 23, 42, 0.05), 0 1px 3px rgba(15, 23, 42, 0.04)",
};

const emptyForm = () => ({
  event_id: "",
  channel: "email",
  recipient_usernames: [],
  enabled: true,
});

export default function CustomerOrgNotifications() {
  const [events, setEvents] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm());

  useEffect(() => {
    document.title = "Organization · Notifications";
  }, []);

  const eventLabelById = useMemo(() => {
    const map = {};
    for (const e of events) {
      map[e.event_id] = e.label || e.event_id;
    }
    return map;
  }, [events]);

  const userLabelByUsername = useMemo(() => {
    const map = {};
    for (const u of users) {
      const uname = u.username || u.user_id;
      if (!uname) continue;
      const name = u.name || u.email || uname;
      map[uname] = name === uname ? uname : `${name} (${uname})`;
    }
    return map;
  }, [users]);

  const configuredEventIds = useMemo(() => {
    const set = new Set();
    for (const s of subscriptions) {
      if (editingId && s.subscription_id === editingId) continue;
      set.add(`${s.event_id}::${s.channel || "email"}`);
    }
    return set;
  }, [subscriptions, editingId]);

  const availableEvents = useMemo(() => {
    return events.filter((e) => {
      const key = `${e.event_id}::email`;
      return !configuredEventIds.has(key);
    });
  }, [events, configuredEventIds]);

  async function reload() {
    setLoading(true);
    setErr("");
    try {
      const [ev, sub, usr] = await Promise.all([
        authedGet("/customer/notification-events"),
        authedGet("/customer/notification-subscriptions"),
        authedGet("/customer/users?limit=100"),
      ]);
      setEvents(ev.items || []);
      setSubscriptions(sub.items || []);
      setUsers(usr.items || []);
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    reload();
  }, []);

  function openCreate() {
    setEditingId(null);
    setFormError("");
    const first = availableEvents[0];
    setForm({
      ...emptyForm(),
      event_id: first?.event_id || "",
    });
    setShowForm(true);
  }

  function openEdit(sub) {
    setEditingId(sub.subscription_id);
    setFormError("");
    setForm({
      event_id: sub.event_id || "",
      channel: sub.channel || "email",
      recipient_usernames: [...(sub.recipient_usernames || [])],
      enabled: sub.enabled !== false,
    });
    setShowForm(true);
  }

  function closeForm() {
    setShowForm(false);
    setEditingId(null);
    setFormError("");
    setForm(emptyForm());
  }

  function toggleRecipient(username) {
    setForm((prev) => {
      const has = prev.recipient_usernames.includes(username);
      return {
        ...prev,
        recipient_usernames: has
          ? prev.recipient_usernames.filter((u) => u !== username)
          : [...prev.recipient_usernames, username],
      };
    });
  }

  async function handleSave() {
    setSaving(true);
    setFormError("");
    try {
      const payload = {
        event_id: form.event_id,
        channel: "email",
        recipient_usernames: form.recipient_usernames,
        enabled: !!form.enabled,
      };
      if (editingId) {
        await authedPut(
          `/customer/notification-subscriptions/${encodeURIComponent(editingId)}`,
          payload
        );
      } else {
        await authedPost("/customer/notification-subscriptions", payload);
      }
      closeForm();
      await reload();
    } catch (e) {
      setFormError(String(e.message || e));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(sub) {
    const label = eventLabelById[sub.event_id] || sub.event_id;
    if (
      !window.confirm(
        `Remove email notifications for “${label}”? Recipients will no longer be emailed for this event.`
      )
    ) {
      return;
    }
    try {
      await authedDelete(
        `/customer/notification-subscriptions/${encodeURIComponent(sub.subscription_id)}`
      );
      if (editingId === sub.subscription_id) closeForm();
      await reload();
    } catch (e) {
      alert(e.message || "Failed to delete subscription");
    }
  }

  const formEventOptions = editingId
    ? events
    : availableEvents.length
      ? availableEvents
      : events;

  if (loading) return <p>Loading…</p>;
  if (err) return <p style={{ color: "red" }}>{err}</p>;

  return (
    <div>
      <div
        style={{
          paddingBottom: 18,
          marginBottom: 4,
          borderBottom: "1px solid #e5e7eb",
        }}
      >
        <h1 style={{ margin: 0 }}>Notifications</h1>
        <div style={{ color: "#6b7280", fontSize: 14, marginTop: 4 }}>
          Choose which organization users receive email when selected events occur.
          Delivery for these events will be enabled as each emit path is wired.
        </div>
      </div>

      <section style={{ ...SECTION_CARD, marginBottom: 24 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 12,
            gap: 12,
          }}
        >
          <h3 style={{ margin: 0 }}>Email subscriptions</h3>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            <button
              type="button"
              onClick={openCreate}
              disabled={!availableEvents.length && !editingId}
              title={
                !availableEvents.length
                  ? "All available events already have a subscription"
                  : undefined
              }
            >
              Add subscription
            </button>
            <InfoTip text={TIPS.add} align="end" />
          </span>
        </div>

        <table style={adminStyles.table}>
          <thead>
            <tr>
              <ThWithTip tip={TIPS.event}>Event</ThWithTip>
              <ThWithTip tip={TIPS.channel}>Channel</ThWithTip>
              <ThWithTip tip={TIPS.recipients}>Recipients</ThWithTip>
              <ThWithTip tip={TIPS.status}>Status</ThWithTip>
              <ThWithTip tip={TIPS.actions}>Actions</ThWithTip>
            </tr>
          </thead>
          <tbody>
            {subscriptions.length === 0 && (
              <tr>
                <td
                  colSpan={5}
                  style={{ ...adminStyles.td, textAlign: "center", color: "#6b7280" }}
                >
                  No subscriptions yet. Add one to start routing email alerts.
                </td>
              </tr>
            )}
            {subscriptions.map((s) => (
              <tr key={s.subscription_id}>
                <td style={adminStyles.td}>
                  <div style={{ fontWeight: 600 }}>
                    {eventLabelById[s.event_id] || s.event_id}
                  </div>
                  <div style={{ color: "#6b7280", fontSize: 12 }}>{s.event_id}</div>
                </td>
                <td style={adminStyles.td}>{s.channel || "email"}</td>
                <td style={adminStyles.td}>
                  {(s.recipient_usernames || []).length
                    ? (s.recipient_usernames || [])
                        .map((u) => userLabelByUsername[u] || u)
                        .join(", ")
                    : "—"}
                </td>
                <td style={adminStyles.td}>
                  <span
                    style={{
                      padding: "2px 6px",
                      borderRadius: 6,
                      background: s.enabled !== false ? "#dcfce7" : "#fee2e2",
                      color: s.enabled !== false ? "#166534" : "#991b1b",
                      fontWeight: 600,
                      fontSize: 12,
                    }}
                  >
                    {s.enabled !== false ? "Enabled" : "Disabled"}
                  </span>
                </td>
                <td style={adminStyles.td}>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <button type="button" onClick={() => openEdit(s)}>
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => void handleDelete(s)}
                      style={{ color: "#991b1b", borderColor: "#dc2626" }}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {showForm && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15, 23, 42, 0.35)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1100,
            padding: 16,
          }}
          onClick={closeForm}
        >
          <div
            role="dialog"
            aria-modal="true"
            style={{
              width: "min(560px, 100%)",
              maxHeight: "90vh",
              overflow: "auto",
              background: "#fff",
              borderRadius: 12,
              border: "1px solid #e5e7eb",
              padding: 20,
              boxShadow: "0 20px 40px rgba(0,0,0,0.15)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ marginTop: 0, marginBottom: 8 }}>
              {editingId ? "Edit subscription" : "Add subscription"}
            </h2>
            <p style={{ marginTop: 0, color: "#6b7280", fontSize: 14 }}>
              Select an event and the users who should receive email when it happens.
            </p>

            <label
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                fontSize: 13,
                marginBottom: 6,
              }}
            >
              Event
              <InfoTip text={TIPS.formEvent} />
            </label>
            <select
              value={form.event_id}
              disabled={!!editingId}
              onChange={(e) => setForm((p) => ({ ...p, event_id: e.target.value }))}
              style={{ width: "100%", height: 36, marginBottom: 14 }}
            >
              {!form.event_id && <option value="">Select an event…</option>}
              {formEventOptions.map((e) => (
                <option key={e.event_id} value={e.event_id}>
                  {e.label}
                </option>
              ))}
            </select>
            {form.event_id && (
              <p style={{ marginTop: -8, marginBottom: 14, color: "#6b7280", fontSize: 13 }}>
                {events.find((e) => e.event_id === form.event_id)?.description || ""}
              </p>
            )}

            <label
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                fontSize: 13,
                marginBottom: 6,
              }}
            >
              Channel
              <InfoTip text={TIPS.channel} />
            </label>
            <input
              value="Email"
              disabled
              style={{ width: "100%", height: 36, marginBottom: 14 }}
            />

            <label
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                fontSize: 13,
                marginBottom: 6,
              }}
            >
              Recipients
              <InfoTip text={TIPS.formRecipients} />
            </label>
            <div
              style={{
                border: "1px solid #e5e7eb",
                borderRadius: 8,
                maxHeight: 220,
                overflow: "auto",
                marginBottom: 14,
              }}
            >
              {users.length === 0 ? (
                <div style={{ padding: 12, color: "#6b7280", fontSize: 13 }}>
                  No users in this organization yet.
                </div>
              ) : (
                users.map((u) => {
                  const uname = u.username || u.user_id;
                  if (!uname) return null;
                  return (
                    <label
                      key={uname}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        padding: "8px 12px",
                        borderBottom: "1px solid #f3f4f6",
                        cursor: "pointer",
                        fontSize: 13,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={form.recipient_usernames.includes(uname)}
                        onChange={() => toggleRecipient(uname)}
                      />
                      <span>
                        <strong>{u.name || uname}</strong>
                        {u.email ? (
                          <span style={{ color: "#6b7280" }}> · {u.email}</span>
                        ) : null}
                      </span>
                    </label>
                  );
                })
              )}
            </div>

            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                fontSize: 13,
                marginBottom: 16,
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={!!form.enabled}
                onChange={(e) =>
                  setForm((p) => ({ ...p, enabled: e.target.checked }))
                }
              />
              Enabled
              <InfoTip text={TIPS.formEnabled} />
            </label>

            {formError && (
              <p style={{ color: "#991b1b", marginTop: 0 }}>{formError}</p>
            )}

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button type="button" onClick={closeForm} disabled={saving}>
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void handleSave()}
                disabled={
                  saving ||
                  !form.event_id ||
                  form.recipient_usernames.length === 0
                }
              >
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
